import {
  referenceStates,
  type Completeness,
  type DocumentRecord,
  type ElementReference,
  type IssueRecord,
  type ModelReference,
  type ReferenceState,
} from "./types";

export function countReferenceStates(
  references: readonly { referenceState: ReferenceState }[],
): Record<ReferenceState, number> {
  const counts = Object.fromEntries(
    referenceStates.map((state) => [state, 0]),
  ) as Record<ReferenceState, number>;

  for (const reference of references) counts[reference.referenceState] += 1;
  return counts;
}

export function deriveCompleteness(states: readonly ReferenceState[]): Completeness {
  return states.every((state) => state === "resolved") ? "complete" : "partial";
}

export const VIRTUALIZATION_THRESHOLD = 250;
export const RENDER_WINDOW_SIZE = 100;

export interface RenderWindow<T> {
  records: readonly T[];
  omitted: number;
  requiresVirtualization: boolean;
}

export function getRenderWindow<T>(records: readonly T[]): RenderWindow<T> {
  if (records.length <= VIRTUALIZATION_THRESHOLD) {
    return { records, omitted: 0, requiresVirtualization: false };
  }
  return {
    records: records.slice(0, RENDER_WINDOW_SIZE),
    omitted: records.length - RENDER_WINDOW_SIZE,
    requiresVirtualization: true,
  };
}

export function sortDocuments(
  documents: readonly DocumentRecord[],
): DocumentRecord[] {
  return [...documents].sort((left, right) => {
    const dateOrder = right.modifiedAt.localeCompare(left.modifiedAt);
    return dateOrder || left.documentNumber.localeCompare(right.documentNumber);
  });
}

export function filterIssues(
  issues: readonly IssueRecord[],
  query: string,
  workflowState: IssueRecord["workflowState"] | "all",
): IssueRecord[] {
  const normalized = query.trim().toLocaleLowerCase("en");
  return issues.filter((issue) => {
    const matchesState = workflowState === "all" || issue.workflowState === workflowState;
    const haystack = `${issue.externalIdentifier} ${issue.title} ${issue.assignee}`.toLocaleLowerCase("en");
    return matchesState && (!normalized || haystack.includes(normalized));
  });
}

export function retainedFieldCount(record: {
  unknownFields: Readonly<Record<string, unknown>>;
}): number {
  return Object.keys(record.unknownFields).length;
}

export interface ElementReferenceAudit {
  referenceState: ReferenceState;
  catalogMatch: boolean;
  revisionDrift: boolean;
}

export function auditElementReference(
  reference: ElementReference,
  catalog: readonly ElementReference[],
): ElementReferenceAudit {
  const sameElement = catalog.filter(
    (candidate) => candidate.elementIdentifier === reference.elementIdentifier,
  );
  return {
    referenceState: reference.referenceState,
    catalogMatch: sameElement.some(
      (candidate) => candidate.modelRevisionIdentifier === reference.modelRevisionIdentifier,
    ),
    revisionDrift: sameElement.some(
      (candidate) => candidate.modelRevisionIdentifier !== reference.modelRevisionIdentifier,
    ),
  };
}
